import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {tint} from 'theme/helper';
import {Icon} from 'shared/helpers';
import {MoreInfoStatic} from 'shared/components/MoreInfo/MoreInfo';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {FaceDraftProvider} from 'components/customer/Product/Drawer/FaceDraftContext';
import {FaceHeightInput} from 'components/customer/Product/Drawer/FaceHeightInput';
import {useComponentFaces} from 'components/customer/Product/Drawer/helpers/useComponentFaces';
import {useComponentFaceDrag} from 'components/customer/Product/Drawer/helpers/useComponentFaceDrag';
import {FaceEdge} from 'components/customer/Product/Drawer/helpers/useDragGesture';
import {stageHeight} from 'components/customer/Product/Drawer/helpers';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';
import {ComponentFacePresets} from 'components/customer/Product/Drawer/ComponentFacePresets';
import {SelectedFace} from 'components/customer/Product/Drawer/SelectedFace';
import {
    Card,
    CardHeader,
    Grip,
    InfoText,
    ResetButton,
    Spacer,
} from 'components/customer/Product/Drawer/layoutStyles';

const LAYOUT_INFO = `Click a face to select it, then type its height. The faces left on auto share whatever is left over, and gaps are drawn to scale.`;

// the same width the cabinet is drawn in, so the two layouts sit the same way
const BOX_WIDTH = 300;
const RAIL_WIDTH = 26;
const LEFT_PAD = RAIL_WIDTH + 4;
// folded, the drawing takes what the panel leaves, between these
const COMPACT_STAGE = 180;
const COMPACT_STAGE_MAX = 400;
// keeps a very shallow face from vanishing
const MIN_FACE_PIXELS = 14;
const ROOMY_PIXELS = 34;
const HANDLE_HEIGHT = 14;

const format = (value: number) => String(Math.round(value * 10) / 10);

// The faces of a standalone drawer face panel, drawn to scale. There is no
// carcase and no runners here, so the panel is only its faces and the gaps
// between them.
export const DrawerFaceLayout = ({fieldset}: {fieldset: Fieldset}) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const compact = useCompactLayout();
    const {
        faces,
        total,
        gap,
        fieldName,
        setHeight,
        setAuto,
        makeAllEqual,
        applyPreset,
    } = useComponentFaces(fieldset);

    const selectHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setSelectedIndex(Number(event.currentTarget.dataset.index));
        },
        []
    );

    // the total the fieldset gives has the gaps taken out, but they still
    // occupy the panel, so the drawing is scaled against the whole of it
    const {box, ratio} = useMemo(() => {
        const panel = total + gap * Math.max(0, faces.length - 1);
        const box = stageHeight(panel, compact);

        return {box, ratio: panel > 0 ? box / panel : 0};
    }, [total, gap, faces.length, compact]);

    const {dragging, preview, startDrag, nudge} = useComponentFaceDrag({
        faces,
        total,
        ratio,
        setHeight,
    });

    // a drag is previewed here until it ends, so the form sees one write
    const drawn = useMemo(() => {
        let cursor = 0;

        return faces.map((face) => {
            const height = preview?.heights[face.index] ?? face.height;
            const pixels = Math.max(MIN_FACE_PIXELS, height * ratio);
            const offset = cursor;

            cursor += (height + gap) * ratio;

            return {
                ...face,
                height,
                offset,
                pixels,
                isRoomy: pixels >= ROOMY_PIXELS,
                // the dragged face is the one that ends up set
                isAuto: face.isAuto && face.index !== preview?.dragged,
            };
        });
    }, [faces, ratio, gap, preview]);

    const allAuto = faces.length > 0 && faces.every((face) => face.isAuto);
    const safeIndex = Math.min(selectedIndex, Math.max(0, faces.length - 1));

    // handles sit on the selected face only, and never on the one face still
    // absorbing the remainder
    const handles = useMemo(() => {
        const face = drawn[Number(safeIndex)];

        if (!face || face.isLocked) return [];

        return [
            ...(safeIndex > 0
                ? [{edge: 'top' as FaceEdge, offset: face.offset}]
                : []),
            ...(safeIndex < drawn.length - 1
                ? [
                      {
                          edge: 'bottom' as FaceEdge,
                          offset: face.offset + face.pixels,
                      },
                  ]
                : []),
        ];
    }, [drawn, safeIndex]);

    if (faces.length === 0) return null;

    return (
        <Card data-cy="drawer-face-layout">
            <CardHeader>
                Drawer Layout
                <MoreInfoStatic
                    hoverText={<InfoText>{LAYOUT_INFO}</InfoText>}
                />
                <Spacer />
                {faces.length > 1 ? (
                    <ResetButton
                        variant="link"
                        className="button-blue btn-sm"
                        data-cy="drawer-make-equal"
                        onClick={makeAllEqual}
                        disabled={allAuto}>
                        <Icon iconName="Reverse-Refresh.svg" color="white" />
                        <span>Make all equal</span>
                    </ResetButton>
                ) : null}
            </CardHeader>
            <Body>
                <ComponentFacePresets
                    faces={faces}
                    total={total}
                    onApply={applyPreset}
                />
                {/* the drawing and the panel edit the same height, so the half-typed text is shared rather than held by either */}
                <FaceDraftProvider>
                    <Stage
                        data-cy="drawer-face-stage"
                        $compact={compact}
                        style={{height: box}}>
                        <Rail
                            title="Total face height"
                            aria-label={`Total face height ${format(
                                total
                            )} mm`}>
                            <RailEnd />
                            <RailLine />
                            <RailLabel>{format(total)} mm</RailLabel>
                            <RailLine />
                            <RailEnd />
                        </Rail>
                        {drawn.map((face) => (
                            <Face
                                key={face.index}
                                data-index={face.index}
                                data-cy={`drawer-face-${face.index}`}
                                onClick={selectHandler}
                                $selected={face.index === safeIndex}
                                $auto={face.isAuto}
                                style={{top: face.offset, height: face.pixels}}
                                title={`Drawer ${face.index + 1}`}>
                                <FaceHeightInput
                                    runnerIndex={face.index}
                                    value={face.height}
                                    pixels={face.pixels}
                                    disabled={face.isLocked}
                                    onSet={setHeight}
                                />
                                <Spacer />
                                {face.isRoomy ? (
                                    <AutoBadge
                                        data-cy={`drawer-face-badge-${face.index}`}
                                        $set={!face.isAuto}
                                        title={
                                            face.isAuto
                                                ? 'Sharing the space left over'
                                                : 'Held at this height. Auto in the panel hands it back to the share'
                                        }>
                                        {face.isAuto ? 'AUTO' : 'FIXED'}
                                    </AutoBadge>
                                ) : null}
                            </Face>
                        ))}
                        {handles.map((handle) => (
                            <Handle
                                key={handle.edge}
                                data-index={safeIndex}
                                data-edge={handle.edge}
                                data-cy={`drawer-handle-${handle.edge}`}
                                onPointerDown={startDrag}
                                onKeyDown={nudge}
                                tabIndex={0}
                                role="separator"
                                aria-label={`${
                                    handle.edge === 'top'
                                        ? 'Raise or lower the top'
                                        : 'Raise or lower the bottom'
                                } of drawer ${safeIndex + 1}`}
                                style={{
                                    top: handle.offset - HANDLE_HEIGHT / 2,
                                }}
                                $dragging={dragging === handle.edge}>
                                <Grip $dragging={dragging === handle.edge} />
                            </Handle>
                        ))}
                    </Stage>
                    <SelectedFace
                        faces={faces}
                        fieldName={fieldName}
                        selectedIndex={safeIndex}
                        onSetHeight={setHeight}
                        onSetAuto={setAuto}
                    />
                </FaceDraftProvider>
            </Body>
        </Card>
    );
};

const Body = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px 14px 20px;
    align-items: flex-start;
    flex-wrap: wrap;
`;

const Stage = styled.div<{$compact: boolean}>`
    position: relative;
    /* shrinks before the panel does; folded, it takes what the panel leaves */
    ${({$compact}) =>
        $compact
            ? `flex: 1 1 ${COMPACT_STAGE}px; max-width: ${COMPACT_STAGE_MAX}px;`
            : `flex-grow: 0;
               flex-shrink: 1;
               flex-basis: ${LEFT_PAD + BOX_WIDTH}px;
               max-width: ${LEFT_PAD + BOX_WIDTH}px;`}
    min-width: 0;
    box-sizing: content-box;
`;

const Rail = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: ${RAIL_WIDTH}px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #6b7480;
    font-size: 11px;
`;

const RailEnd = styled.div`
    width: 11px;
    height: 1px;
    background: #9aa3ae;
`;

const RailLine = styled.div`
    flex: 1;
    width: 1px;
    background: #9aa3ae;
`;

const RailLabel = styled.div`
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    padding: 4px 0;
    white-space: nowrap;
`;

const Handle = styled.div<{$dragging: boolean}>`
    /* without this the browser claims the gesture for scrolling on touch */
    touch-action: none;
    position: absolute;
    left: ${LEFT_PAD}px;
    right: 0;
    z-index: 4;
    box-sizing: border-box;
    height: ${HANDLE_HEIGHT}px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ns-resize;
    outline: none;
    background: transparent;

    &:focus-visible span {
        box-shadow: 0 0 0 2px ${({theme}) => theme.colors.primary?.main};
    }
`;

const Face = styled.div<{$selected: boolean; $auto: boolean}>`
    position: absolute;
    left: ${LEFT_PAD}px;
    right: 0;
    z-index: ${({$selected}) => ($selected ? 2 : 1)};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
    cursor: pointer;
    border-radius: 2px;
    border: 1px solid
        ${({theme, $selected}) =>
            $selected
                ? theme.colors.secondary?.main
                : 'rgba(32, 67, 128, 0.3)'};
    box-shadow: ${({$selected, theme}) =>
        $selected ? `0 0 0 1px ${theme.colors.secondary?.main}` : 'none'};
    /* the same three states the carcase draws: the one being edited is a
       clean white sheet, a face still sharing the space is hatched */
    background: ${({$selected, $auto, theme}) =>
        $selected
            ? '#fff'
            : $auto
              ? `repeating-linear-gradient(135deg, ${tint(
                    theme.colors.primary?.main,
                    0.05
                )} 0 9px, ${tint(theme.colors.primary?.main, 0.11)} 9px 18px)`
              : tint(theme.colors.primary?.main, 0.05)};
`;

const AutoBadge = styled.span<{$set: boolean}>`
    flex: none;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 2px 6px;
    border-radius: 9px;
    border: 1px solid rgba(32, 67, 128, 0.25);
    background: ${({$set}) => ($set ? 'transparent' : 'rgba(32,67,128,0.08)')};
    color: ${({$set, theme}) => ($set ? '#8c95a1' : theme.colors.primary?.main)};
`;
